import React, { createContext, useContext, useState, useEffect } from 'react';
import Api from '../utils/Api';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDecode } from '../hooks/useDecode';

const AuthContext = createContext(null);
const adminPrefix = process.env.REACT_APP_ADMIN_ROUTE_PREFIX || 'admin';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate()
  
  const [api, setApi] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // Initialize API with token getter
  useEffect(() => {
  
    setApi(Api(() => user?.token));
  }, [user]);

  // Check for stored auth data on component mount
  useEffect(() => {
    const initializeAuth = async () => {
       
      try {
        const storedAuth = localStorage.getItem('auth');
        if (storedAuth) {
          const authData = JSON.parse(storedAuth);
          setUser(authData);
        }
      } catch (error) {
        localStorage.removeItem('auth');
      } finally {
        setLoading(false); 
      }
    };
     
    initializeAuth();
  }, []);

  const handleApiError = (error) => {
    if (error?.status === 401) {
      logout();
      return false;
    } 
    // Handle validation errors
    if (error?.validationErrors?.length > 0) {
      setFormErrors(error.validationErrors);
    } else if(error?.data?.message) {
       setFormErrors(error?.data?.message || 'An error occurred');
    }else if(error?.message){
      setFormErrors({ general: <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-2" role="alert"><strong className="font-bold">Error: </strong><span className="block sm:inline">Oops! Something went wrong.
An unexpected error occurred. Please try again later or contact support if the issue persists.</span></div> });
       
    } 
     
    return false; 
  };
 
  const login = async (credentials) => {
    setFormErrors();
    const response = await api.call('login', 'POST', credentials, false);
  
    if (response?.status === 200) {
      const auth = response.data.auth;
      setUser(auth);
      localStorage.setItem('auth', JSON.stringify(auth));
      return { status: 200 };
    }
  
    if (response?.error?.status === 422 && response?.error?.validationErrors) {
      return {
        status: 422,
        errors: response.error.validationErrors, // 👈 must be a plain object!
      };
    }else{
      return handleApiError(response.error);
    }
  
    // return {
    //   status: response?.error?.status || 500,
    //   error: { message: response?.error?.message || 'Unknown error' },
    // };
  };

  const logout = () => {
    setUser(null);
    const isAdmin = isSuperAdmin();
    localStorage.removeItem('auth');
    if(isAdmin){
      navigate(adminPrefix+'/login');
    } else {
      navigate('/login');
    }
  };

  const updateUser = (user) => {
    user.token = getToken();
    setUser(user);
    localStorage.setItem('auth', JSON.stringify(user));
  }

  const isAuthenticated = () => {
    return !!user?.token;
  };

  const isSuperAdmin = () => {
    return user?.role_id === 1;
  };

  const getToken = () => {
    return user?.token;
  };

  const { decodedValue: googleMapApiKey } = useDecode(
    user?.google_map_api_key,
    "map",
  );
 
  if (loading) {
    return (
       <LoadingSpinner />
    );
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      updateUser,
      login, 
      logout, 
      isAuthenticated, 
      isSuperAdmin, 
      getToken, 
      formErrors,
      setFormErrors,
      api,
      loading,
      googleMapApiKey
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 